import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Bell, BellOff, Play, Clock, VolumeX, RefreshCw } from "lucide-react";
import axios from "axios";
import { useIsDispatcher } from "@/hooks/useIsDispatcher";

// Preset ringtones available in the app
const PRESET_RINGTONES = [
  { id: "default", name: "Default", url: null },
  { id: "emergency", name: "Emergency", url: "/sounds/emergency.mp3" },
  { id: "double-beep", name: "Double Beep", url: "/sounds/double-beep.mp3" },
  { id: "triple-alert", name: "Triple Alert", url: "/sounds/triple-alert.mp3" }
];

// Hotline incoming-call alert bands. Mirrors backend/hotline_bands.py — the
// `type` is the push notificationType, which is also the ringtones key, so each
// band can sound different. "every" is the catch-all for rounds 2+, but After
// Round 5 / After Round 9 override it so their own ringtone still fires.
const HOTLINE_BANDS = [
  { key: "first", type: "hotlineRound1",    label: "Incoming call",  hint: "the first ring" },
  { key: "every", type: "hotlineEvery",     label: "Every round",    hint: "every time they ring back" },
  { key: "r234",  type: "hotlineRounds234", label: "Rounds 2, 3, 4", hint: "" },
  { key: "from5", type: "hotlineFrom5",     label: "After Round 5",  hint: "from the 5th ring on — overrides “Every round”, so this ringtone tells you it's round 5" },
  { key: "from9", type: "hotlineFrom9",     label: "After Round 9",  hint: "from the 9th ring on — overrides “Every round” and “After Round 5”" },
];
// The only band "every" covers, and therefore disables. from5/from9 stay
// switchable while it's on: they outrank it so their ringtone can mark the round.
const HOTLINE_BANDS_UNDER_EVERY = ["r234"];
// Switching one of these on while "every" is on takes those rounds away from it,
// so it asks first — the alert for round 5+ changes sound, which is the point,
// but only if you meant it.
const HOTLINE_OVERRIDE_BANDS = ["from5", "from9"];
const HOTLINE_OVERRIDE_COPY = {
  from5: {
    title: "Let After Round 5 take over from “Every round”?",
    body: "“Every round” is on, so rounds 2 and up already alert you. Turning this on hands rounds 5 and up to After Round 5 instead: they fire as their own alert, with their own ringtone, so you can hear that a caller has rung five times. Rounds 2, 3 and 4 stay on “Every round”.",
  },
  from9: {
    title: "Let After Round 9 take over from “Every round”?",
    body: "“Every round” is on, so rounds 2 and up already alert you. Turning this on hands rounds 9 and up to After Round 9 instead: they fire as their own alert, with their own ringtone, so you can hear that a caller has rung nine times. The rounds below 9 are unaffected.",
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    allEnabled: true,  // Delivery on/off, independent of the push subscription
    normalCalls: true,
    priorityCalls: true,
    priorityCallUpdates: true,
    transmitted1085: true,  // Controls BOTH initial calls and updates
    transmitted1013: true,  // Controls BOTH initial calls and updates
    callUpdates: true,
    patrolActivity: true,  // Patrol car login/logout notifications
    perpetratorUpdates: true,  // Perpetrator profile notifications
    sound: true,
    popup: true,
    // Preset ringtones (stored as URL paths like "/sounds/emergency.mp3" or null for default)
    ringtones: {
      priorityCalls: null,
      transmitted1085: null,
      transmitted1013: null,
      normalCalls: null,
      callUpdates: null
    },
    // Hotline incoming-call alerts, per round band. {bandKey: "on"|"off"}.
    hotlineRounds: {},
    // Mute settings
    mutedUntil: null  // ISO timestamp when mute ends, or null if not muted
  });

  const isDispatcher = useIsDispatcher();
  const [tab, setTab] = useState('calls');
  const [pushLog, setPushLog] = useState(null);  // null = not loaded yet
  const [pushLogLoading, setPushLogLoading] = useState(false);
  // Band key awaiting the "this overrides Every round" confirmation, or null.
  const [pendingOverrideBand, setPendingOverrideBand] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [muteTimeRemaining, setMuteTimeRemaining] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [isAndroid, setIsAndroid] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [permState, setPermState] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const lastPermRef = useRef(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const audioRefs = useRef({});

  // Detect Android device
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    setIsAndroid(/android/i.test(userAgent));
  }, []);

  // Check for active push subscription on mount
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  // Re-read the permission whenever we come back to the foreground.
  //
  // Fixing a "blocked" state happens *outside* this page — in the installed APK it means toggling
  // the app's notification switch in phone Settings, which is what Chrome mirrors for a TWA. Without
  // this, you'd return to a page still showing the value it read at mount, so a successful fix looks
  // like it did nothing and the only way through is a full restart of the app. Re-checking on
  // visibility means the page heals itself the moment you switch back.
  useEffect(() => {
    if (typeof Notification === 'undefined') return;

    const syncPermission = () => {
      if (document.hidden) return;
      const current = Notification.permission;
      if (current === lastPermRef.current) return;
      lastPermRef.current = current;
      setPermState(current);
      // Newly allowed while we were away — pick the subscription up too.
      if (current === 'granted') checkSubscriptionStatus();
    };

    document.addEventListener('visibilitychange', syncPermission);
    window.addEventListener('focus', syncPermission);
    return () => {
      document.removeEventListener('visibilitychange', syncPermission);
      window.removeEventListener('focus', syncPermission);
    };
  }, []);

  // Mute timer countdown
  useEffect(() => {
    if (!settings.mutedUntil) {
      setMuteTimeRemaining(null);
      return;
    }

    const updateRemaining = async () => {
      const now = new Date();
      const muteEnd = new Date(settings.mutedUntil);
      const diff = muteEnd - now;

      if (diff <= 0) {
        // Mute expired, clear it
        const newSettings = { ...settings, mutedUntil: null };
        setSettings(newSettings);
        localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
        setMuteTimeRemaining(null);

        // Sync to server
        try {
          await axios.put(`${API}/users/me/notifications`, {
            mutedUntil: null
          }, { withCredentials: true });
        } catch (error) {
          console.error('Failed to sync unmute to server:', error);
        }

        toast.success('Notifications unmuted');
        return;
      }

      // Format remaining time
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) {
        setMuteTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setMuteTimeRemaining(`${minutes}m`);
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [settings.mutedUntil]);

  const handleMute = async (minutes) => {
    const muteEnd = new Date(Date.now() + minutes * 60 * 1000);
    const newSettings = { ...settings, mutedUntil: muteEnd.toISOString() };
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));

    // Save to IndexedDB for service worker
    if ('indexedDB' in window) {
      const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        store.put({ id: 'notificationSettings', data: newSettings });
      };
    }

    // Sync to server
    try {
      await axios.put(`${API}/users/me/notifications`, {
        mutedUntil: muteEnd.toISOString()
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to sync mute to server:', error);
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const timeStr = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}${mins > 0 ? ` ${mins} min` : ''}` : `${mins} minutes`;
    toast.success(`Notifications muted for ${timeStr}`);
  };

  const handleUnmute = async () => {
    const newSettings = { ...settings, mutedUntil: null };
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));

    // Save to IndexedDB for service worker
    if ('indexedDB' in window) {
      const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        store.put({ id: 'notificationSettings', data: newSettings });
      };
    }

    // Sync to server
    try {
      await axios.put(`${API}/users/me/notifications`, {
        mutedUntil: null
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to sync unmute to server:', error);
    }

    toast.success('Notifications unmuted');
  };

  const checkSubscriptionStatus = async () => {
    try {
      // Check if service worker has an active subscription
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setHasActiveSubscription(subscription !== null);
      } else {
        setHasActiveSubscription(false);
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      setHasActiveSubscription(false);
    }
  };

  // Check whether a newer installed-app (APK) version is available. The installed version is read
  // from localStorage (captured from the TWA launch URL ?appVersion=N in index.js).
  const handleCheckUpdate = async () => {
    setCheckingUpdate(true);
    setUpdateInfo(null);
    try {
      const res = await fetch('/app/version.json?t=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) throw new Error('no version file');
      const latest = await res.json();
      const installed = parseInt(localStorage.getItem('installedApkVersion') || '0', 10);
      const isNewer = (latest.versionCode || 0) > installed;
      setUpdateInfo({ ...latest, available: isNewer });
      if (!isNewer) toast.success("You're on the latest version");
    } catch (e) {
      toast.error('Could not check for updates right now');
    } finally {
      setCheckingUpdate(false);
    }
  };

  useEffect(() => {
    // Load settings from localStorage first
    const saved = localStorage.getItem('notificationSettings');
    let localSettings = {};
    if (saved) {
      try {
        localSettings = JSON.parse(saved);
        setSettings(prev => ({ ...prev, ...localSettings }));
      } catch (e) {
        console.error('Failed to parse notification settings');
      }
    }
    
    // Then sync with server settings (admin-controlled preferences)
    const syncWithServer = async () => {
      try {
        const response = await axios.get(`${API}/users/me/notifications`, { withCredentials: true });
        if (response.data) {
          // Merge server settings with local settings
          const serverSettings = response.data;
          const newSettings = {
            ...localSettings,
            enabled: serverSettings.enabled,
            allEnabled: serverSettings.allEnabled !== false,
            normalCalls: serverSettings.normalCalls,
            priorityCalls: serverSettings.priorityCalls,
            priorityCallUpdates: serverSettings.priorityCallUpdates,
            transmitted1085: serverSettings.transmitted1085,
            transmitted1013: serverSettings.transmitted1013,
            callUpdates: serverSettings.callUpdates,
            patrolActivity: serverSettings.patrolActivity,
            perpetratorUpdates: serverSettings.perpetratorUpdates,
            hotlineRounds: serverSettings.hotlineRounds || {},
            mutedUntil: serverSettings.mutedUntil || null
          };

          setSettings(prev => ({ ...prev, ...newSettings }));
          setCanEdit(serverSettings.canEdit !== false);

          // Save to localStorage
          localStorage.setItem('notificationSettings', JSON.stringify(newSettings));

          // Save to IndexedDB for service worker
          if ('indexedDB' in window) {
            const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
            request.onsuccess = (event) => {
              const db = event.target.result;
              const transaction = db.transaction(['settings'], 'readwrite');
              const store = transaction.objectStore('settings');
              store.put({ id: 'notificationSettings', data: newSettings });
            };
          }
        }
      } catch (error) {
        console.log('Could not sync notification settings with server:', error);
      }
    };

    syncWithServer();
  }, []);

  const handleToggle = async (key) => {
    // The master switch reflects whether push is ACTUALLY active (a live subscription),
    // not just the saved preference — so a device that lost its subscription shows OFF and
    // a single tap re-subscribes (no off-wait-on dance).
    const currentlyOn = key === 'enabled'
      ? (settings.enabled && hasActiveSubscription)
      : settings[key];
    const newSettings = { ...settings, [key]: !currentlyOn };

    // Handle subscribe/unsubscribe for the main toggle
    if (key === 'enabled') {
      if (newSettings[key]) {
        // Turning ON - subscribe
        const success = await handleSubscribe();
        if (!success) {
          // Subscribe failed, revert the setting
          newSettings[key] = false;
        }
      } else {
        // Turning OFF - unsubscribe
        await handleUnsubscribe();
      }
    }
    
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
    
    // Also save to IndexedDB for service worker
    if ('indexedDB' in window) {
      const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        store.put({ id: 'notificationSettings', data: newSettings });
      };
    }
    
    // Sync notification type settings to server (including enabled toggle)
    const syncKeys = ['enabled', 'allEnabled', 'normalCalls', 'priorityCalls', 'priorityCallUpdates', 'transmitted1085', 'transmitted1013', 'callUpdates', 'patrolActivity', 'perpetratorUpdates'];
    if (syncKeys.includes(key)) {
      try {
        await axios.put(`${API}/users/me/notifications`, {
          [key]: newSettings[key]
        }, { withCredentials: true });
      } catch (error) {
        console.error('Failed to sync notification setting with server:', error);
      }
    }
    
    // Only show success toast for non-enabled toggles (enabled has its own toasts)
    if (key === 'allEnabled') {
      // The generic label builder would render this as "all Enabled enabled".
      toast.success(newSettings[key]
        ? 'Notifications enabled'
        : 'All notifications silenced, including 10-85 and 10-13');
    } else if (key !== 'enabled') {
      toast.success(`${key.replace(/([A-Z])/g, ' $1').trim()} ${newSettings[key] ? 'enabled' : 'disabled'}`);
    }
    
    // Refresh subscription status when toggling main switch
    if (key === 'enabled') {
      setTimeout(() => checkSubscriptionStatus(), 500);
    }
  };

  const handleSubscribe = async () => {
    try {
      toast.info('Subscribing to push notifications...');
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied — see "Notification Permission" above for how to re-enable it.');
        setHasActiveSubscription(false);
        return false;
      }

      // Check if service worker is registered
      if (!('serviceWorker' in navigator)) {
        toast.error('Service workers not supported in this browser');
        return false;
      }

      // Get VAPID public key from backend
      let vapidPublicKey = null;
      try {
        const configResponse = await axios.get(`${API}/app-config`, { withCredentials: true });
        vapidPublicKey = configResponse.data.vapid_public_key;
      } catch (error) {
        console.error('Failed to get VAPID key from backend:', error);
        toast.error('Push notifications not configured on server. Contact your administrator.');
        return false;
      }
      
      if (!vapidPublicKey) {
        toast.error('VAPID key not set on server. Push notifications cannot be enabled.');
        return false;
      }

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;

      // Unsubscribe from any existing subscription first
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subscriptionObject = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionObject.endpoint,
        auth_key: subscriptionObject.keys.auth,
        p256dh_key: subscriptionObject.keys.p256dh
      }, { withCredentials: true });

      localStorage.removeItem('push_opted_out');
      toast.success('Push notifications enabled successfully!');
      setHasActiveSubscription(true);
      return true;
    } catch (error) {
      console.error('Subscribe error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Failed to enable push notifications: ' + errorMsg);
      setHasActiveSubscription(false);
      return false;
    }
  };

  // Explicit "Allow Notifications" prompt trigger. requestPermission() only shows the OS/browser
  // prompt while the permission is still 'default'; once 'denied' no web code can re-show it
  // (a hard Chrome/Android rule), so we point at the app's notification switch instead.
  //
  // In the installed APK this is a TWA, so Chrome does not keep its own permission for the origin —
  // it mirrors the Android app's. Chrome's own Site settings shows "managed by <app>" with nothing
  // to set, so pointing users there is a dead end. The mirror can also go stale (seen 2026-08-03 on
  // CCHD: every Android layer read allowed while the web layer still reported denied, cached from a
  // fresh install before the permission was granted). What re-syncs it is toggling the app's
  // notification switch in phone Settings off and back on — hence the wording below.
  const handleRequestPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Notifications are not supported on this browser');
      return;
    }
    try {
      const result = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      setPermState(result);
      lastPermRef.current = result;
      if (result === 'granted') {
        await handleSubscribe();
      } else if (result === 'denied') {
        toast.error('Notifications are blocked. In the installed app: phone Settings → Apps → this app → Notifications, toggle it OFF then back ON, then return here.');
      } else {
        toast.info('Notification prompt dismissed — tap "Allow Notifications" again to retry.');
      }
    } catch (e) {
      toast.error('Could not request notification permission');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      toast.info('Unsubscribing from push notifications...');
      
      // Unsubscribe from push manager
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Delete subscription from backend
      await axios.post(`${API}/push/unsubscribe`, {}, {
        withCredentials: true
      });

      localStorage.setItem('push_opted_out', '1');
      toast.success('Push notifications disabled');
      setHasActiveSubscription(false);
    } catch (error) {
      console.error('Unsubscribe error:', error);
      // Even if backend fails, we unsubscribed from browser
      setHasActiveSubscription(false);
      toast.warning('Notifications disabled (backend cleanup may have failed)');
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Toggle one hotline round band. Unlike the other rows this is a key inside a
  // dict rather than a top-level boolean, so it needs its own handler — but it
  // persists the same three ways: state, localStorage, IndexedDB (for the service
  // worker), and the server.
  const handleHotlineBand = (band) => {
    const current = settings.hotlineRounds || {};
    const turningOn = current[band] !== 'on';
    // Switching this on while "every" is on doesn't just add an alert — it moves
    // those rounds off "every" onto a different sound. Confirm before it lands.
    if (turningOn && current.every === 'on' && HOTLINE_OVERRIDE_BANDS.includes(band)) {
      setPendingOverrideBand(band);
      return;
    }
    applyHotlineBand(band);
  };

  const applyHotlineBand = async (band) => {
    const current = settings.hotlineRounds || {};
    const turningOn = current[band] !== 'on';
    const nextBands = { ...current, [band]: turningOn ? 'on' : 'off' };
    const newSettings = { ...settings, hotlineRounds: nextBands };

    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));

    if ('indexedDB' in window) {
      const request = indexedDB.open('CHSPSettings', 2);
      request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        store.put({ id: 'notificationSettings', data: newSettings });
      };
    }

    try {
      await axios.put(`${API}/users/me/notifications`, { hotlineRounds: nextBands }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to sync hotline round setting with server:', error);
      toast.error('Could not save — try again');
      return;
    }
    toast.success(`${HOTLINE_BANDS.find(b => b.key === band)?.label || band} ${turningOn ? 'on' : 'off'}`);
  };

  // The delivery log — the only way to tell "no notification was sent" apart
  // from "one was sent and didn't arrive".
  const loadPushLog = async () => {
    setPushLogLoading(true);
    try {
      const res = await axios.get(`${API}/incoming-calls/hotline-push-log`, { withCredentials: true });
      setPushLog(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error('Could not load recent alerts');
      setPushLog([]);
    } finally {
      setPushLogLoading(false);
    }
  };

  const handleRingtoneChange = (notificationType, presetId) => {
    const preset = PRESET_RINGTONES.find(p => p.id === presetId);
    const newSettings = {
      ...settings,
      ringtones: {
        ...settings.ringtones,
        [notificationType]: preset?.url || null
      }
    };
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));

    // Save to IndexedDB for service worker
    if ('indexedDB' in window) {
      const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => { const db = event.target.result; if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'id' }); };
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        store.put({ id: 'notificationSettings', data: newSettings });
      };
    }

    toast.success(`Ringtone changed to ${preset?.name || 'Default'}`);
  };

  const handleTestRingtone = (notificationType) => {
    const ringtoneUrl = settings.ringtones?.[notificationType];

    // Stop any currently playing audio
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    if (ringtoneUrl) {
      // Play preset ringtone
      const audio = new Audio(ringtoneUrl);
      audioRefs.current[notificationType] = audio;
      audio.play().catch(err => {
        console.error('Error playing ringtone:', err);
        toast.error('Failed to play ringtone');
      });
    } else {
      // Play default notification sound
      toast.info('Using default notification sound');
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKXh7bVjHQU2jdXuyXksBSp+zPLaizsKGGS56+mmVhMNRp/d8bllHwYuhM/z1YUzBxdmsOvsolIRDEee3O+0Yh0FN4zT7MZ4LQUngsr0240+ChhjuOvnplYSC0Oe3fG5ZSAFLoTP89SFMgcWZ7Ds56NSEAxGnt3zuWUgBS+Dz/PUhS8HFWex6+akUhENRZ7e8bZjHgU3i9Psw3YqBSp+y/PajDsJFmO48OajUxELRZ3c87pkHwYug8/z1IQyBxVmsOznoVIQC0Wd3fCzYh4FNojR68l2KgUrgMv03Iw6CRdluvDqpVIRC0Se3vKzYh0HM4bP88d2KwYqf8ry2oo6ChZmuPDnplcSDESd3O+zYh4FN4jP68Z1LQYqfsvy2Yk6ChViv/DqpVQSC0Sd3u+zYx4FNYjQ68h2LAYqfs31240+ChdjuO/lpVQSCkOd3e+yYyAGNYjQ68h1LAYqfszz24w9ChVju+7kpVQSCkOd3e+yYx8GNYfP7MZ1LAcpfszy24s9ChVjuuzkpVMSCkOd3e+yYx8GNYfP7A==');
      audio.play().catch(() => {});
    }
  };

  const handleTestNotification = async () => {
    try {
      toast.info('Sending test notification...');
      const response = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Test notification sent!');
      // Subscription is working, update status
      setHasActiveSubscription(true);
    } catch (error) {
      console.error('Test notification error:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      if (error.response?.status === 404 || errorDetail.includes('No active')) {
        toast.error('No active subscriptions found. Toggle notifications OFF then ON to create a new subscription.');
        setHasActiveSubscription(false);
      } else if (errorDetail.includes('410') || errorDetail.includes('expired') || errorDetail.includes('unsubscribed')) {
        toast.error('Subscription expired. Toggle notifications OFF, wait 2 seconds, then toggle ON.');
        setHasActiveSubscription(false);
      } else {
        toast.error(errorDetail || 'Failed to send test notification');
      }
    }
  };

  // Get current preset ID from URL
  const getPresetIdFromUrl = (url) => {
    if (!url) return "default";
    const preset = PRESET_RINGTONES.find(p => p.url === url);
    return preset?.id || "default";
  };

  // Ringtone control component with preset selector
  const RingtoneControl = ({ notificationType, label }) => {
    const currentUrl = settings.ringtones?.[notificationType];
    const currentPresetId = getPresetIdFromUrl(currentUrl);
    const currentPreset = PRESET_RINGTONES.find(p => p.id === currentPresetId);

    return (
      <div className="mt-3 p-3 bg-slate-900/50 rounded border border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-300">Ringtone</span>
          {currentPresetId !== "default" && (
            <span className="text-xs text-green-400">✓ {currentPreset?.name}</span>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={currentPresetId}
            onChange={(e) => handleRingtoneChange(notificationType, e.target.value)}
            className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {PRESET_RINGTONES.map(preset => (
              <option key={preset.id} value={preset.id}>
                {preset.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleTestRingtone(notificationType)}
            title="Test ringtone"
          >
            <Play className="w-3 h-3" />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {currentPresetId === "default" ? 'Using default notification sound' : `Using ${currentPreset?.name} sound`}
        </p>
      </div>
    );
  };

  // True only when push is genuinely active on THIS device (preference on + live subscription).
  const masterActive = settings.enabled && hasActiveSubscription;

  const tabTriggerCls =
    "flex-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm";

  // Hotline incoming-call alerts, one row per round band.
  const renderHotlineRounds = () => {
    const bands = settings.hotlineRounds || {};
    const everyOn = bands.every === 'on';

    return (
      <div className="space-y-4">
        {!settings.enabled && (
          <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
            <p className="text-xs text-amber-300">
              Notifications are switched off on this device — turn them on under Calls first.
            </p>
          </div>
        )}

        {HOTLINE_BANDS.map((band) => {
          const on = bands[band.key] === 'on';
          // "Every round" already covers rounds 2-4, so that band greys out
          // while it's on rather than silently contradicting it. The from5/from9
          // escalation bands are NOT covered — they outrank it.
          const coveredByEvery = everyOn && HOTLINE_BANDS_UNDER_EVERY.includes(band.key);
          const disabled = !settings.enabled || !canEdit || coveredByEvery;

          return (
            <div
              key={band.key}
              className={`p-4 bg-slate-950 rounded-lg border border-slate-800 ${disabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Label htmlFor={`hotline-${band.key}`} className="text-base text-blue-300">
                    {band.label}
                  </Label>
                  <p className="text-xs text-slate-400 mt-1">
                    {coveredByEvery ? 'Covered by “Every round”' : band.hint}
                  </p>
                </div>
                <Switch
                  id={`hotline-${band.key}`}
                  checked={on && !coveredByEvery}
                  onCheckedChange={() => handleHotlineBand(band.key)}
                  disabled={disabled}
                />
              </div>
              {on && !coveredByEvery && (
                <RingtoneControl notificationType={band.type} label={band.label} />
              )}
            </div>
          );
        })}

        {/* Delivery log — the only way to tell "no notification was sent" apart
            from "one was sent and didn't arrive". */}
        <div className="p-4 bg-slate-950 rounded-lg border border-slate-800">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <Label className="text-base font-semibold">Recent Alerts</Label>
              <p className="text-xs text-slate-400 mt-1">
                Every hotline push attempt, including the ones that were skipped and why.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={loadPushLog} disabled={pushLogLoading}>
              <RefreshCw className={`w-3 h-3 mr-1 ${pushLogLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {pushLog === null ? (
            <p className="text-xs text-slate-500">Tap Refresh to load.</p>
          ) : pushLog.length === 0 ? (
            <p className="text-xs text-slate-500">Nothing logged yet.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {pushLog.map((row, i) => {
                const sent = row.outcome === 'sent';
                const failed = (row.outcome || '').startsWith('failed');
                return (
                  // Stacked, not two columns. The outcome is a sentence ("no band
                  // matched this round", or a whole exception), and beside a
                  // shrink-0 column it squeezed the caller number down to nothing.
                  <div key={i} className="p-2 bg-slate-900 rounded text-xs space-y-0.5">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-slate-200 font-medium truncate">{row.caller_number || '—'}</span>
                      {row.round_count > 1 && (
                        <span className="text-slate-400 shrink-0">round {row.round_count}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {row.at ? new Date(row.at).toLocaleTimeString('en-US', { timeZone: 'America/New_York' }) : ''}
                      {row.trigger ? ` · ${row.trigger}` : ''}
                      {row.band ? ` · ${row.band}` : ''}
                    </div>
                    <div className={`text-[10px] break-words ${sent ? 'text-green-400' : failed ? 'text-red-400' : 'text-slate-400'}`}>
                      {row.outcome}
                      {sent && row.recipients ? ` — ${row.recipients} device${row.recipients === 1 ? '' : 's'}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Confirm an escalation band taking rounds away from "Every round". */}
        <AlertDialog open={!!pendingOverrideBand} onOpenChange={(open) => { if (!open) setPendingOverrideBand(null); }}>
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-200">
                {HOTLINE_OVERRIDE_COPY[pendingOverrideBand]?.title}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {HOTLINE_OVERRIDE_COPY[pendingOverrideBand]?.body}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
              <Button
                className="bg-blue-600 hover:bg-blue-500 text-white"
                onClick={() => { const b = pendingOverrideBand; setPendingOverrideBand(null); applyHotlineBand(b); }}
              >
                Turn on
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              {masterActive ? (
                <Bell className="w-6 h-6 text-blue-500" />
              ) : (
                <BellOff className="w-6 h-6 text-slate-500" />
              )}
              <CardTitle className="text-xl uppercase tracking-wider">Notification Settings</CardTitle>
            </div>
            {/* Stacked, not inline: these are two independent states and a
                second badge on the same row pushed the header apart. */}
            <div className="flex flex-col items-end gap-1 shrink-0">
              {/* Subscription Status Badge */}
              {hasActiveSubscription ? (
                <Badge variant="default" className="bg-green-600">
                  <Bell className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <BellOff className="w-3 h-3 mr-1" />
                  Inactive
                </Badge>
              )}
              {/* Silenced is a separate axis from subscribed — a device can be
                  connected and still delivering nothing, so say so rather than
                  letting a green "Active" imply alerts are getting through. */}
              {settings.allEnabled === false && (
                <Badge variant="destructive">
                  <BellOff className="w-3 h-3 mr-1" />
                  Silenced
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-2">
            Configure which notifications you want to receive
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dispatchers get a second page for hotline incoming-call alerts.
              Everyone else sees the page exactly as before, no tab strip. */}
          {isDispatcher && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full">
                <TabsTrigger value="calls" className={tabTriggerCls}>Calls</TabsTrigger>
                <TabsTrigger value="incoming" className={tabTriggerCls}>Incoming Calls</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {isDispatcher && tab === 'incoming' && renderHotlineRounds()}

          {tab === 'calls' && (<>
          {/* Notification permission (explicit prompt trigger) */}
          <div className="p-4 bg-slate-950 rounded-lg border border-slate-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex-1 min-w-[12rem]">
                <Label className="text-base font-semibold">Notification Permission</Label>
                <p className="text-xs text-slate-400 mt-1">
                  {permState === 'granted' && 'Allowed ✓'}
                  {permState === 'default' && 'Not yet allowed — tap to get the prompt'}
                  {permState === 'denied' && 'Blocked — in the installed app, go to phone Settings → Apps → this app → Notifications and toggle it OFF then back ON, then come back here. (In a browser instead: tap the icon left of the address bar → Permissions → Notifications.)'}
                  {permState === 'unsupported' && 'Not supported on this browser'}
                </p>
              </div>
              {permState !== 'granted' && (
                <Button
                  onClick={handleRequestPermission}
                  disabled={permState === 'unsupported'}
                  className="bg-blue-600 hover:bg-blue-700 text-white shrink-0 whitespace-nowrap"
                >
                  Allow Notifications
                </Button>
              )}
            </div>
          </div>

          {/* Master switch — owns the push subscription itself. Turning this off
              unsubscribes the device; turning it on re-subscribes. */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border ${!settings.enabled && !canEdit ? 'border-red-800/50' : 'border-slate-800'}`}>
            <div>
              <Label htmlFor="enabled" className="text-base font-semibold">
                Master Switch
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Connects this device to push notifications. Turning it off unsubscribes
                the device entirely.
              </p>
              {!settings.enabled && !canEdit && (
                <p className="text-xs text-red-400 mt-1">
                  🔒 Disabled by administrator
                </p>
              )}
            </div>
            <Switch
              id="enabled"
              checked={masterActive}
              onCheckedChange={() => handleToggle('enabled')}
              disabled={!canEdit}
            />
          </div>

          {/* Delivery switch — independent of the subscription above. Off means
              nothing is delivered at all, 10-85 and 10-13 included. */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border ${settings.allEnabled === false ? 'border-red-800/50' : 'border-slate-800'}`}>
            <div>
              <Label htmlFor="allEnabled" className="text-base font-semibold">
                Notifications Enabled
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Turn off to silence everything — including 10-85 and 10-13 — while
                staying subscribed, so switching back on takes effect right away.
              </p>
              {settings.allEnabled === false && (
                <p className="text-xs text-red-400 mt-1">
                  All notifications are off, emergencies included.
                </p>
              )}
            </div>
            <Switch
              id="allEnabled"
              checked={settings.allEnabled !== false}
              onCheckedChange={() => handleToggle('allEnabled')}
              disabled={!canEdit}
            />
          </div>

          {/* Test Notification Button */}
          <div className="p-4 bg-blue-950/20 border border-blue-900/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold text-blue-400">Test Notifications</Label>
                <p className="text-xs text-slate-400 mt-1">
                  Send a test push notification to verify your settings
                </p>
              </div>
              <Button 
                onClick={handleTestNotification}
                disabled={!masterActive}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Bell className="w-4 h-4 mr-2" />
                Send Test
              </Button>
            </div>
          </div>

          {/* App Updates (installed Android app) */}
          {isAndroid && (
            <div className="p-4 bg-purple-950/20 border border-purple-900/50 rounded-lg">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1 min-w-[12rem]">
                  <Label className="text-base font-semibold text-purple-400">App Updates</Label>
                  <p className="text-xs text-slate-400 mt-1">
                    Check for a newer version of the installed app
                  </p>
                </div>
                <Button
                  onClick={handleCheckUpdate}
                  disabled={checkingUpdate}
                  className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 whitespace-nowrap"
                >
                  {checkingUpdate ? "Checking…" : "Check for Updates"}
                </Button>
              </div>
              {updateInfo?.available && (
                <div className="mt-3 p-3 rounded bg-purple-900/30 border border-purple-700/50">
                  <p className="text-sm text-purple-200">
                    Update available: v{updateInfo.versionName || updateInfo.versionCode}
                    {updateInfo.notes ? ` — ${updateInfo.notes}` : ""}
                  </p>
                  <Button
                    onClick={() => { try { localStorage.setItem('installedApkVersion', String(updateInfo.versionCode)); } catch (e) {} window.location.href = updateInfo.apkUrl; }}
                    className="mt-2 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Download &amp; Install
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    After it downloads, tap the file to install (you may need to allow installs from this app).
                  </p>
                </div>
              )}
              {updateInfo && !updateInfo.available && (
                <p className="mt-2 text-xs text-emerald-400">
                  You're on the latest version (v{updateInfo.versionName || updateInfo.versionCode}).
                </p>
              )}
            </div>
          )}

          {/* Android Banner Setup Guide */}
          {isAndroid && settings.enabled && (
            <div className="p-4 bg-emerald-950/20 border border-emerald-700/50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="text-2xl">📱</div>
                <div className="flex-1">
                  <Label className="text-base font-semibold text-emerald-400">Android Banner Notifications</Label>
                  <p className="text-xs text-slate-400 mt-1 mb-3">
                    To see notifications drop down from the top of your screen (like texts or calls), you need to enable banner notifications in Android settings:
                  </p>
                  <ol className="text-xs text-slate-300 space-y-2 list-decimal list-inside mb-3">
                    <li>Open your phone's <span className="text-white font-medium">Settings</span> app</li>
                    <li>Go to <span className="text-white font-medium">Apps → CHSP</span> (or Chrome if not installed as app)</li>
                    <li>Tap <span className="text-white font-medium">Notifications</span></li>
                    <li>Under Categories, tap <span className="text-white font-medium">General</span> (or "All CHSP notifications")</li>
                    <li>Enable <span className="text-white font-medium">Pop on screen</span> or <span className="text-white font-medium">Banner</span></li>
                    <li>Set importance to <span className="text-white font-medium">Urgent</span> or <span className="text-white font-medium">High</span></li>
                  </ol>
                  <p className="text-xs text-slate-500 italic">
                    Note: Steps may vary slightly by phone manufacturer (Samsung, OnePlus, Pixel, etc.)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mute Section */}
          <div className={`p-4 rounded-lg border ${settings.mutedUntil ? 'bg-amber-950/30 border-amber-700' : 'bg-slate-950 border-slate-800'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <VolumeX className={`w-5 h-5 ${settings.mutedUntil ? 'text-amber-400' : 'text-slate-400'}`} />
                <Label className="text-base font-semibold">
                  {settings.mutedUntil ? 'Notifications Muted' : 'Mute Notifications'}
                </Label>
              </div>
              {settings.mutedUntil && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-600">
                    <Clock className="w-3 h-3 mr-1" />
                    {muteTimeRemaining} remaining
                  </Badge>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleUnmute}
                    className="text-amber-400 border-amber-600 hover:bg-amber-900/30"
                  >
                    Unmute
                  </Button>
                </div>
              )}
            </div>
            {!settings.mutedUntil && (
              <>
                <p className="text-xs text-slate-400 mb-3">
                  Temporarily silence all notifications
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleMute(15)}>15 min</Button>
                  <Button size="sm" variant="outline" onClick={() => handleMute(30)}>30 min</Button>
                  <Button size="sm" variant="outline" onClick={() => handleMute(60)}>1 hour</Button>
                  <Button size="sm" variant="outline" onClick={() => handleMute(120)}>2 hours</Button>
                  <Button size="sm" variant="outline" onClick={() => handleMute(240)}>4 hours</Button>
                  <Button size="sm" variant="outline" onClick={() => handleMute(480)}>8 hours</Button>
                </div>
              </>
            )}
          </div>

          {/* Popup Notifications */}
          <div className="flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-slate-800">
            <div>
              <Label htmlFor="popup" className="text-base font-semibold">
                Popup Notifications
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Show toast popups in the app when notifications arrive
              </p>
            </div>
            <Switch
              id="popup"
              checked={settings.popup}
              onCheckedChange={() => handleToggle('popup')}
              disabled={!settings.enabled}
            />
          </div>

          {/* Sound */}
          <div className="flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-slate-800">
            <div>
              <Label htmlFor="sound" className="text-base font-semibold">
                Notification Sound
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Play sound when notifications arrive
              </p>
            </div>
            <Switch
              id="sound"
              checked={settings.sound}
              onCheckedChange={() => handleToggle('sound')}
              disabled={!settings.enabled}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
              Notification Types
            </h3>
            {!canEdit && (
              <div className="mb-4 p-3 bg-amber-950/30 border border-amber-800/50 rounded-lg">
                <p className="text-sm text-amber-400 flex items-center gap-2">
                  <span>🔒</span>
                  Your notification settings are managed by an administrator.
                </p>
              </div>
            )}
          </div>

          {/* Normal Calls */}
          <div className={`p-4 bg-slate-950 rounded-lg border border-slate-800 ${!canEdit ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="normalCalls" className="text-base">
                  Normal Calls
                </Label>
                <p className="text-xs text-slate-400 mt-1">
                  Regular live calls (not priority)
                </p>
              </div>
              <Switch
                id="normalCalls"
                checked={settings.normalCalls}
                onCheckedChange={() => handleToggle('normalCalls')}
                disabled={!settings.enabled || !canEdit}
              />
            </div>
            {settings.normalCalls && <RingtoneControl notificationType="normalCalls" label="Normal Calls" />}
          </div>

          {/* Priority Calls */}
          <div className={`p-4 bg-slate-950 rounded-lg border border-slate-800 ${!canEdit ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="priorityCalls" className="text-base">
                  Priority Calls
                </Label>
                <p className="text-xs text-slate-400 mt-1">
                  High-priority live calls
                </p>
              </div>
              <Switch
                id="priorityCalls"
                checked={settings.priorityCalls}
                onCheckedChange={() => handleToggle('priorityCalls')}
                disabled={!settings.enabled || !canEdit}
              />
            </div>
            {settings.priorityCalls && <RingtoneControl notificationType="priorityCalls" label="Priority Calls" />}
          </div>

          {/* Priority Call Updates */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-slate-800 ${!canEdit ? 'opacity-60' : ''}`}>
            <div>
              <Label htmlFor="priorityCallUpdates" className="text-base">
                Priority Call Updates
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Updates to priority live calls
              </p>
            </div>
            <Switch
              id="priorityCallUpdates"
              checked={settings.priorityCallUpdates}
              onCheckedChange={() => handleToggle('priorityCallUpdates')}
              disabled={!settings.enabled || !canEdit}
            />
          </div>

          {/* 10-85 - Always on, cannot be disabled */}
          <div className="p-4 bg-slate-950 rounded-lg border border-orange-600/30 opacity-80">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="transmitted1085" className="text-base text-orange-400">
                  10-85 (All)
                </Label>
                <p className="text-xs text-slate-400 mt-1">
                  Emergency assistance - initial calls and all updates
                </p>
                <p className="text-[10px] text-orange-500 mt-1">Mandatory — cannot be disabled</p>
              </div>
              <Switch
                id="transmitted1085"
                checked={true}
                disabled={true}
              />
            </div>
            <RingtoneControl notificationType="transmitted1085" label="10-85" />
          </div>

          {/* 10-13 - Always on, cannot be disabled */}
          <div className="p-4 bg-slate-950 rounded-lg border border-red-600/30 opacity-80">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="transmitted1013" className="text-base text-red-400">
                  10-13 (All)
                </Label>
                <p className="text-xs text-slate-400 mt-1">
                  Officer needs assistance - initial calls and all updates
                </p>
                <p className="text-[10px] text-red-500 mt-1">Mandatory — cannot be disabled</p>
              </div>
              <Switch
                id="transmitted1013"
                checked={true}
                disabled={true}
              />
            </div>
            <RingtoneControl notificationType="transmitted1013" label="10-13" />
          </div>

          {/* Call Updates */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-slate-800 ${!canEdit ? 'opacity-60' : ''}`}>
            <div>
              <Label htmlFor="callUpdates" className="text-base">
                Call Updates
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                General updates to live calls
              </p>
            </div>
            <Switch
              id="callUpdates"
              checked={settings.callUpdates}
              onCheckedChange={() => handleToggle('callUpdates')}
              disabled={!settings.enabled || !canEdit}
            />
          </div>

          {/* Patrol Activity */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-emerald-600/30 ${!canEdit ? 'opacity-60' : ''}`}>
            <div>
              <Label htmlFor="patrolActivity" className="text-base text-emerald-400">
                Patrol Activity
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Patrol car login/logout notifications
              </p>
            </div>
            <Switch
              id="patrolActivity"
              checked={settings.patrolActivity}
              onCheckedChange={() => handleToggle('patrolActivity')}
              disabled={!settings.enabled || !canEdit}
            />
          </div>

          {/* Profile Updates */}
          <div className={`flex items-center justify-between p-4 bg-slate-950 rounded-lg border border-purple-600/30 ${!canEdit ? 'opacity-60' : ''}`}>
            <div>
              <Label htmlFor="perpetratorUpdates" className="text-base text-purple-400">
                Profile Updates
              </Label>
              <p className="text-xs text-slate-400 mt-1">
                Profile linked, ready for arrest, and arrest notifications
              </p>
            </div>
            <Switch
              id="perpetratorUpdates"
              checked={settings.perpetratorUpdates}
              onCheckedChange={() => handleToggle('perpetratorUpdates')}
              disabled={!settings.enabled || !canEdit}
            />
          </div>
          </>)}
        </CardContent>
      </Card>
    </div>
  );
}
